<template>
<div class="learning-screen">
  <top-navigation
    :current-start-time="currentSentence ? currentSentence.start : 0"
    :current-end-time="currentSentence ? currentSentence.end : 0"
    :sentence-index="drill.sentenceIndex"
    :sentence-count="drill.sentenceCount"
    @toggle-speech-mode="toggleSpeakingMode"
    @show-tutorial="resetTutorial"
    @show-settings="showSettings"
    @show-share="openShare"
    @show-feedback="openFeedback('idea')"
  />

  <swipe-area class="content-area" @swipe-left="onNext" @swipe-right="onPrev">
    <div class="player-container">
      <video-player-section
        ref="playerSection"
        :show-play-button="!isPlaying && (drill.status === 'ready' || drill.status === 'listen')"
        @overlay-click="onPlayerOverlayClick"
      />
    </div>

    <drill-card
      :drill="drill"
      :loading-stalled="loadingStalled"
      :sentence="currentSentence"
      :sentences="sentences"
      :score-result="scoreResult"
      :recognized-text="attemptText"
      :video-title="videoTitle"
      :progress="listenProgress"
      :playback-rate="playbackRate"
      :timeline-no-transition="timelineSnapping"
      :subtitle="currentSubtitleText"
      :subtitle-source="subtitleSource"
      :has-recording="!!attemptAudioUrl"
      :playing-self="playingSelf"
      :speak-cap-ms="speakCapMs"
      :speak-attempt-key="speakAttemptKey"
      :challenge="challenge"
      :playing-challenge="playingChallenge"
      @hear-self="onHearSelf"
      @hear-challenge="onHearChallenge"
      @share="openShare"
      @report-problem="openFeedback('bug')"
      @card-tap="onCardTap"
      @next="onNext"
      @retry="onRetry"
      @replay="onReplay"
      @choose-video="showSettings"
      @retry-load="onRetryLoad"
      @mic-retry="onMicRetry"
      @hear-again="onHearAgain"
      @end-attempt="onEndAttemptManual"
      @reset-attempt="onResetAttempt"
    />
  </swipe-area>

  <action-bar
    :prev-ghost="barConfig.prevGhost"
    :next-ghost="barConfig.nextGhost"
    :secondary="barConfig.secondary"
    :primary="barConfig.primary"
    :countdown="barConfig.countdown"
    :style="navRingStyle"
    @prev="onPrev"
    @next="onNext"
    @secondary-action="onSecondaryAction"
    @primary-action="onPrimaryAction"
  />

  <!-- Settings modal and tutorial live in App.vue so every screen reaches them -->
</div>
</template>

<script>
import { mapState } from 'vuex';
import VideoPlayerSection from "./player/VideoPlayerSection";
import TopNavigation from "./ui/TopNavigation";
import ActionBar from "./ui/ActionBar";
import DrillCard from "./drill/DrillCard.vue";
import SwipeArea from "./ui/SwipeArea.vue";
import { getTranscript, getSegmentText } from '../services/transcript';
import { sentencesFromTranscript, findSentenceIndex } from '../services/sentences';
import { getVideoTitle } from '../services/videoinfo';
import { firstName } from '../services/share';
import { buildFeedbackContext } from '../services/feedback';
import { useChallenge, loadChallenge } from '../composables/useChallenge';
import { primeAudioContext } from '../services/passSound';
import * as micStream from '../services/micStream';
import { getAsrEngine, ASR_ENGINE_MOONSHINE } from '../services/asr';
import { preload as preloadMoonshine } from '../services/asrMoonshine';
import DrillController from '../services/drillController';
import DrillRunner, { track, drillStep } from '../services/drillRunner';
import BackgroundGuard from '../services/backgroundGuard';
import PositionSync from '../services/positionSync';
import { barConfig } from '../services/actionBar';
import {
  createDrillState,
  transition,
  ACTIVE_STATUSES,
  ADVANCE_MIN_MS
} from '../services/drill';

export default {
  name: 'LearningScreen',
  components: {
    VideoPlayerSection,
    TopNavigation,
    ActionBar,
    DrillCard,
    SwipeArea
  },
  setup() {
    return useChallenge();
  },
  data() {
    return {
      drill: createDrillState(),
      sentences: [],
      scoreResult: null,
      attemptText: '',
      videoTitle: '',
      subtitleSegments: [],
      subtitleSource: null,
      attemptAudioUrl: null,
      playingSelf: false,
      // Straight from the controller's cap timer; the card animates the ring
      // over exactly this in CSS, so there is no second clock to drift.
      speakCapMs: null,
      // Bumped per attempt; the card keys the ring <svg> on it to restart
      // the CSS animation.
      speakAttemptKey: 0,
      // BUGFIX-003 §1: true ~12s into an unresolved load, to offer an escape hatch.
      loadingStalled: false,
      // STORY-002 §8c: true for one tick after a seek so the timeline marker
      // snaps instead of gliding. Reactive because a class binding reads it.
      timelineSnapping: false
    };
  },
  created() {
    // Non-reactive instance fields: keep timer handles out of Vue reactivity.
    this._loadingStallTimer = null;

    this.drillController = new DrillController({
      getDrill: () => this.drill,
      getSentence: () => this.currentSentence,
      getLang: () => this.lang,
      getAdvanceDelayMs: () => this.advanceDelayMs,
      send: (event) => this.send(event),
      setMicActive: (v) => {
        if (v) this.runner.trackStep('mic_open');
        return this.$store.dispatch('recognition/setMicActive', v);
      },
      setMicDenied: (v) => this.$store.commit('recognition/SET_MIC_DENIED', v),
      setRecognizedText: (t) => this.$store.dispatch('recognition/setRecognizedText', t),
      onAttemptText: (t) => { this.attemptText = t; },
      onScoreResult: (r) => { this.scoreResult = r; },
      onRecordingUrl: (u) => { this.attemptAudioUrl = u; },
      onPlayingSelf: (v) => { this.playingSelf = v; },
      onSpeakDeadline: (ms) => {
        this.speakCapMs = ms;
        // Null clears precede most fresh starts; bumping on them would
        // restart the ring twice for one attempt.
        if (ms) this.speakAttemptKey++;
      }
    });
    this.runner = new DrillRunner({
      store: this.$store,
      controller: this.drillController,
      getDrill: () => this.drill,
      getSentences: () => this.sentences,
      getLang: () => this.lang,
      isPlaying: () => this.isPlaying,
      send: (event) => this.send(event),
      onSeek: () => this.markTimelineSnap(),
      recordHistory: () => this.recordHistory(),
      loadTranscript: () => this.loadDrillTranscript()
    });
    this.background = new BackgroundGuard({
      store: this.$store,
      controller: this.drillController,
      getDrill: () => this.drill,
      send: (event) => this.send(event),
      // This visit only; practiceTimer counts real drilling time.
      onPageHide: () => this.runner.trackSessionEnd(
        this.$store.state.stats.totalSeconds - (this._practiceSecondsAtMount || 0))
    });
    this.position = new PositionSync({
      store: this.$store,
      getRoute: () => this.$route,
      getRouter: () => this.$router,
      getDrill: () => this.drill,
      getLang: () => this.lang
    });
  },
  async mounted() {
    // Capture the query before any await: loadSavedSettings -> setVideoId ->
    // urlParamKey watcher -> router.replace overwrites $route with stored values.
    const initialQuery = (this.$route && this.$route.query) || {};

    this.challenge = loadChallenge(initialQuery);

    // Baseline for trackSessionEnd; practiceTimer loads totals at store creation.
    this._practiceSecondsAtMount = this.$store.state.stats.totalSeconds;

    window.addEventListener('keyup', this.handleKeyUp);
    window.addEventListener('keydown', this.handleKeyDown);
    // Any tap or key rebuilds a missing chime context, not only the primary
    // button (BUGFIX-010). pointerup, not pointerdown: a touch pointerdown
    // doesn't count as a user activation.
    document.addEventListener('pointerup', primeAudioContext, true);
    document.addEventListener('keydown', primeAudioContext, true);
    this.background.attach();

    if (getAsrEngine() === ASR_ENGINE_MOONSHINE) {
      preloadMoonshine();
    }

    // Warm Silero regardless of engine (endpointing is engine-independent);
    // otherwise the first attempt falls back to RMS VAD.
    micStream.preloadVad();

    try {
      await this.$store.dispatch('settings/loadSavedSettings');
      // URL wins over localStorage. Uses initialQuery because $route has
      // already been overwritten by now (see above).
      this.position.applyUrl(initialQuery);
    } catch (err) {
      console.error('Error initializing in mounted hook:', err);
    }
    this.position.bootstrapping = false;

    this.loadDrillTranscript();
    this.runner.start();

    // A pull that finished before mount never fires the watcher; read it once.
    this.position.applyResumePoint(this.$store.state.auth.resumePoint);

    // Challenge visitors skip the language modal and tutorial: languages came
    // in the link, and friction here loses them.
    if (this.challenge) {
      await this.applyChallengeLanguages(this.$store);
      return;
    }

    var isFirstTime = localStorage.getItem('tutorialCompleted') !== 'true';
    var hasLangs = (function() {
      try {
        var saved = JSON.parse(localStorage.getItem('shadowingAppState') || '{}');
        return !!(saved.targetLanguage && saved.sourceLanguage);
      } catch (e) { return false; }
    }());
    if (isFirstTime || !hasLangs) {
      this.$store.dispatch('settings/openFirstRunSettings', { withTutorial: isFirstTime });
      track('first_run_langs_shown', { category: 'Onboarding', withTutorial: isFirstTime });
    }
  },
  async beforeDestroy() {
    window.removeEventListener('keyup', this.handleKeyUp);
    window.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('pointerup', primeAudioContext, true);
    document.removeEventListener('keydown', primeAudioContext, true);
    this.background.detach();

    this.drillController.destroy();
    this.releaseChallengeAudio();
    this.runner.stop();
    if (this._loadingStallTimer) {
      clearTimeout(this._loadingStallTimer);
      this._loadingStallTimer = null;
    }

    try {
      if (this.$store.state.recognition.speakingMode) {
        await this.$store.dispatch('recognition/setSpeakingMode', false);
      }
      this.$store.dispatch('player/cleanupPlayer');
    } catch (err) {
      console.error('Error in beforeDestroy hook:', err);
    }
  },
  computed: {
    ...mapState('player', ['videoId', 'startTime', 'currentTime', 'playerState', 'playbackRate']),
    ...mapState('recognition', ['speakingMode']),
    ...mapState('settings', ['targetLanguage', 'sourceLanguage']),
    lang() {
      return (this.targetLanguage && this.targetLanguage.code) || 'en';
    },
    currentSentence() {
      return this.sentences[this.drill.sentenceIndex] || null;
    },
    advanceDelayMs() {
      const s = this.currentSentence;
      const durationMs = s ? (s.end - s.start) * 1000 : 0;
      return Math.max(ADVANCE_MIN_MS, durationMs);
    },
    // Machine subtitles are translated per drill sentence by the Worker (same
    // sentence builder, same track), so their spans equal the sentence's and
    // no padding is needed; padding would pull in a short neighbour (BUGFIX-007).
    currentSubtitleText() {
      const s = this.currentSentence;
      if (!s || this.subtitleSegments.length === 0) return '';
      const pad = this.subtitleSource === 'machine' ? 0 : undefined;
      return getSegmentText(this.subtitleSegments, s.start, s.end, pad);
    },
    // STORY-002 §8a: one formula for all active statuses. During speak the
    // video sits at the sentence end, so this parks at ~1; cruise is handled
    // by the clamp.
    listenProgress() {
      const sentence = this.currentSentence;
      if (!sentence || ACTIVE_STATUSES.indexOf(this.drill.status) === -1) return 0;
      const duration = sentence.end - sentence.start;
      if (duration <= 0) return 0;
      const value = (this.currentTime - sentence.start) / duration;
      return Math.max(0, Math.min(1, value));
    },
    // STORY-002: raw intro gap for drill.js (which applies MIN_CRUISE_S).
    // 0 for challenge links: cruising would delay the sentence they were sent.
    cruiseIntroGapS() {
      if (this.challenge) return 0;
      const first = this.sentences[0];
      return first ? first.start : 0;
    },
    // Counts buffering (3) as playing: YouTube buffers after every seek, and a
    // bare === 1 showed a useless "Play" through much of a short sentence
    // (BUGFIX-003 §3).
    isPlaying() {
      return this.playerState === 1 || this.playerState === 3;
    },
    barConfig() {
      return barConfig(this.drill, { isPlaying: this.isPlaying, loadingStalled: this.loadingStalled });
    },
    navRingStyle() {
      return { '--advance-duration': this.advanceDelayMs + 'ms' };
    },
    urlParamKey() {
      return (this.videoId || '') + '|' + (this.startTime || 0) + '|' + (this.drill.sentenceIndex || 0);
    }
  },
  watch: {
    'sourceLanguage.code': function() {
      this.loadSubtitleTranscript();
    },
    videoId(newId) {
      this.sentences = [];
      this.scoreResult = null;
      this.attemptText = '';
      // A challenge is about one clip, so switching video drops it. The id
      // check matters: mounted() sets the link's own video through here. The
      // bootstrapping guard covers the stored-video restore that fires first.
      if (!this.position.bootstrapping && this.challenge && this.challenge.videoId !== newId) {
        this.clearChallenge();
      }
      this.send({ type: 'VIDEO_CHANGED' });
    },
    'targetLanguage.code': function() {
      this.sentences = [];
      this.scoreResult = null;
      this.attemptText = '';
      this.send({ type: 'VIDEO_CHANGED' });
    },
    startTime(newTime) {
      // External Settings change: relocate to the sentence at the new time.
      // Drill-driven seeks match the current sentence and are ignored.
      if (this.sentences.length === 0) return;
      if (this.runner.isCruiseSeek(newTime)) return;
      const currentSentence = this.sentences[this.drill.sentenceIndex];
      if (currentSentence && Math.abs(newTime - currentSentence.start) <= 0.05) return;
      const newIdx = findSentenceIndex(this.sentences, newTime);
      if (newIdx !== -1) {
        this.send({ type: 'STARTTIME_CHANGED', index: newIdx });
      }
    },
    speakingMode(newVal) {
      // Covers the top-nav mic toggle and recognition dying mid-session.
      if (!newVal && ACTIVE_STATUSES.indexOf(this.drill.status) !== -1) {
        const micDenied = this.$store.state.recognition.micDenied;
        this.send({ type: micDenied ? 'MIC_DENIED' : 'SUSPEND' });
      }
    },
    urlParamKey() {
      this.position.onPositionChange();
    },
    '$store.state.auth.resumePoint': function(point) { this.position.applyResumePoint(point); }
  },
  methods: {
    toggleSpeakingMode() {
      this.$store.dispatch('recognition/toggleSpeakingMode');
    },

    showSettings() {
      this.$store.dispatch('settings/openSettings');
    },

    resetTutorial() {
      this.$store.dispatch('settings/openTutorial');
    },

    isTypingTarget(e) {
      const tag = e.target && e.target.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
    },

    handleKeyUp(e) {
      if (this.isTypingTarget(e)) return;
      if (e.keyCode == 32) { // Space key
        e.preventDefault();
        this.onPrimaryAction();
      }
    },

    // Space acts on keyup; its keydown is swallowed so the page can't scroll.
    handleKeyDown(e) {
      if (this.isTypingTarget(e)) return;
      if (e.keyCode == 32 || e.keyCode == 37 || e.keyCode == 39) e.preventDefault();
      if (e.keyCode == 37) this.onPrev();
      if (e.keyCode == 39) this.onNext();
    },

    togglePlayback() {
      if (this.$refs.playerSection) {
        this.$refs.playerSection.togglePlayback();
      }
    },

    onPlayerOverlayClick() {
      if (this.drill.status === 'ready') {
        this.startDrill();
      } else if (this.drill.status === 'listen') {
        this.togglePlayback();
      }
    },

    // A same-object result means the event was invalid for this status.
    send(event) {
      const r = transition(this.drill, event);
      if (r.state === this.drill) return;
      this.runner.trackStep(drillStep(event));
      // Reporting effects get the pre-transition state: TRACK_SEGMENT_COMPLETED
      // fires after sentenceIndex has already advanced.
      const prev = this.drill;
      this.drill = r.state;
      r.effects.forEach(fx => this.runner.run(fx, prev));
    },

    // STORY-002 §8c: disable the marker transition for one tick so a seek
    // snaps instead of gliding. Call synchronously where the seek commits.
    markTimelineSnap() {
      this.timelineSnapping = true;
      this.$nextTick(() => { this.timelineSnapping = false; });
    },

    async loadDrillTranscript() {
      this.loadSubtitleTranscript();
      if (!this.videoId) return;
      const requestVideoId = this.videoId;
      const lang = this.lang;

      // BUGFIX-003 §1: flag a stalled fetch after 12s so the bar offers Retry /
      // Choose video. Not cleared on success: status leaves 'loading', so a late
      // fire is inert.
      this.loadingStalled = false;
      if (this._loadingStallTimer) clearTimeout(this._loadingStallTimer);
      this._loadingStallTimer = setTimeout(() => {
        this.loadingStalled = true;
        // Only a real stall is tracked, since the timer is never cleared.
        if (this.drill.status === 'loading') {
          track('drill_load_stalled', { category: 'Drill', videoId: requestVideoId });
        }
      }, 12000);

      getVideoTitle(requestVideoId).then(title => {
        if (this.videoId === requestVideoId) this.videoTitle = title || '';
      }).catch(() => {
        this.videoTitle = '';
      });

      try {
        const transcript = await getTranscript(requestVideoId, lang);
        // Video or language changed mid-fetch; the newer load owns the state.
        if (this.isStaleDrillLoad(requestVideoId, lang)) return;
        const sentences = sentencesFromTranscript(transcript);
        if (sentences.length === 0) {
          this.send({ type: 'TRANSCRIPT_FAILED', code: 'NO_TRANSCRIPT' });
          return;
        }

        this.sentences = sentences;
        const idx = findSentenceIndex(sentences, this.startTime);
        const sentence = sentences[idx];

        if (Math.abs(this.startTime - sentence.start) > 0.05) {
          this.$store.dispatch('player/setStartTime', sentence.start);
        }

        if (this.challenge && this.challenge.videoId === requestVideoId) {
          this.challenge = Object.assign({}, this.challenge, { sentenceIndex: idx });
        }

        this.send({ type: 'SENTENCES_READY', count: sentences.length, index: idx });
      } catch (e) {
        if (this.isStaleDrillLoad(requestVideoId, lang)) return;
        this.send({ type: 'TRANSCRIPT_FAILED', code: (e && e.code) || 'NETWORK' });
      }
    },

    isStaleDrillLoad(requestVideoId, requestLang) {
      return this.videoId !== requestVideoId || this.lang !== requestLang;
    },

    // Best-effort native-language subtitles; never touches drill state.
    loadSubtitleTranscript() {
      if (!this.videoId) return;
      const src = this.sourceLanguage && this.sourceLanguage.code;
      const tgt = this.lang;

      this.subtitleSegments = [];
      this.subtitleSource = null;

      if (!src || src === tgt) return;

      const requestVideoId = this.videoId;
      const requestLang = src;
      getTranscript(requestVideoId, src, { translate: true, from: tgt }).then(data => {
        if (this.videoId !== requestVideoId || this.sourceLanguage.code !== requestLang) return;
        this.subtitleSegments = data.segments;
        this.subtitleSource = data.translationSource;
      }).catch(() => {});
    },

    async startDrill() {
      this.runner.trackStep('play_tapped');
      primeAudioContext();
      // iOS only honours a playback start inside the live user gesture, and
      // the permission sheet below outlives it, so unlock the player here.
      // Paused immediately: the mic probe must not open while audio plays
      // (nothing has buffered yet, so it is silent).
      this.$store.dispatch('player/resumeVideo');
      this.$store.dispatch('player/pauseVideo');

      await this.$store.dispatch('recognition/setSpeakingMode', true);

      if (this.$store.state.recognition.micDenied) {
        this.send({ type: 'MIC_DENIED' });
      } else if (this.$store.state.recognition.speakingMode) {
        this.send({ type: 'PLAY_TAPPED', introGapS: this.cruiseIntroGapS });
      } else {
        // Neither granted nor denied: the tap did nothing visible.
        this.runner.trackStep('mic_not_started');
      }
    },

    async onMicRetry() {
      this.$store.commit('recognition/SET_MIC_DENIED', false);
      this.$store.commit('recognition/SET_MIC_GRANTED', false);

      const granted = await this.$store.dispatch('recognition/ensureMicPermission');
      if (!granted) {
        this.send({ type: 'MIC_DENIED' });
        return;
      }

      await this.$store.dispatch('recognition/setSpeakingMode', true);
      this.send({ type: 'MIC_GRANTED' });
    },

    recordHistory() {
      // Challenge visitors skipped onboarding; their first score marks them
      // as returning.
      if (this.challenge && localStorage.getItem('tutorialCompleted') !== 'true') {
        try {
          localStorage.setItem('tutorialCompleted', 'true');
        } catch (e) { /* storage unavailable */ }
      }

      const s = this.sentences[this.drill.sentenceIndex];
      if (!s) return;
      this.$store.dispatch('stats/recordSession', {
        videoId: this.videoId,
        segStart: s.start,
        segEnd: s.end,
        lang: this.lang,
        accuracy: this.drill.lastAccuracy,
        refWordCount: this.scoreResult ? this.scoreResult.counts.refTotal : 0,
        practicedAt: new Date().toISOString()
      });
    },

    onPrimaryAction() {
      // Every call here is a user gesture, the only stack iOS lets us rebuild a
      // dead AudioContext from; covers resumes that bypass startDrill().
      primeAudioContext();
      switch (this.drill.status) {
        case 'ready': this.startDrill(); break;
        case 'listen': this.togglePlayback(); break;
        // Reset: submitting is the card's ring, Replay is secondary (STORY-004).
        case 'speak': this.onResetAttempt(); break;
        case 'feedback':
          if (this.drill.replayPending) break;
          // BUGFIX-003 §3: real Pause/Play during replay.
          if (this.drill.replaying) { this.togglePlayback(); break; }
          this.send({ type: 'NEXT_TAPPED' });
          break;
        case 'done': this.send({ type: 'PRACTICE_AGAIN', introGapS: this.cruiseIntroGapS }); break;
        // The card is information-only, so the bar must route these.
        case 'unsupported': this.showSettings(); break;
        case 'micBlocked': this.onMicRetry(); break;
        // Only offered once loadingStalled.
        case 'loading': if (this.loadingStalled) this.showSettings(); break;
        default: break;
      }
    },

    onSecondaryAction() {
      switch (this.drill.status) {
        case 'listen': this.onReplay(); break;
        // STOP_ALL_TIMERS releases the mic, so the iOS route handling applies.
        case 'speak': this.onReplay(); break;
        case 'feedback': this.onRetry(); break;
        case 'unsupported': this.onRetryLoad(); break;
        case 'done': this.showSettings(); break;
        case 'loading': if (this.loadingStalled) this.onRetryLoad(); break;
        default: break;
      }
    },

    onReplay() { this.send({ type: 'REPLAY_TAPPED' }); },
    onPrev() { this.send({ type: 'PREV_TAPPED' }); },
    onNext() { this.send({ type: 'NEXT_TAPPED' }); },
    onRetry() { this.send({ type: 'RETRY_TAPPED' }); },
    onResetAttempt() { this.send({ type: 'RESET_TAPPED' }); },
    onRetryLoad() { this.send({ type: 'RETRY_LOAD' }); },
    onCardTap() { this.send({ type: 'CANCEL_ADVANCE' }); },

    onEndAttemptManual() {
      this.drillController.endAttempt({ manual: true });
    },

    onHearAgain() {
      // Self and original playback must never overlap.
      this.drillController.stopSelfPlayback();
      this.send({ type: 'HEAR_AGAIN' });
    },

    onHearSelf() {
      this.drillController.playSelf(this.attemptAudioUrl);
    },

    /**
     * listen/speak: suspend like backgrounding so no capture runs behind the
     * modal. feedback: only cancel the countdown (SUSPEND would discard the
     * score being shared).
     */
    openShare() {
      if (this.drill.status === 'feedback') {
        this.send({ type: 'CANCEL_ADVANCE' });
      } else if (ACTIVE_STATUSES.indexOf(this.drill.status) !== -1) {
        this.background.suspend();
      }

      const sentence = this.currentSentence;
      const user = this.$store.state.auth.user;
      const passed = this.drill.status === 'feedback' &&
        this.scoreResult && this.scoreResult.accuracy !== null;

      this.$store.dispatch('settings/openShareSheet', {
        videoId: this.videoId,
        start: sentence ? sentence.start : this.startTime,
        lang: this.lang,
        motherTongue: this.sourceLanguage && this.sourceLanguage.code,
        score: passed ? this.scoreResult.accuracy : null,
        name: user ? firstName(user.name) : '',
        sentence: sentence ? sentence.text : '',
        // A getter so the blob is read on opt-in, not pinned in the payload.
        canAttachVoice: passed && !!this.attemptAudioUrl,
        getRecording: () => this.drillController.getAttemptRecording()
      });
    },

    // Context is read before the suspend: SUSPEND resets the status and the attempt.
    openFeedback(type) {
      const context = buildFeedbackContext({
        videoId: this.videoId,
        lang: this.lang,
        sentence: this.currentSentence,
        drill: this.drill,
        scoreResult: this.scoreResult,
        asrEngine: getAsrEngine(),
        signedIn: !!this.$store.state.auth.user
      });
      if (this.drill.status === 'feedback') {
        this.send({ type: 'CANCEL_ADVANCE' });
      } else if (ACTIVE_STATUSES.indexOf(this.drill.status) !== -1) {
        this.background.suspend();
      }
      this.$store.dispatch('settings/openFeedback', { type, context });
    },

    onHearChallenge() {
      this.hearChallenge(url => this.drillController.playSelf(url));
    }
  }
}
</script>

<style scoped>
.learning-screen {
  height: 100vh; /* Fallback */
  height: 100dvh; /* Dynamic viewport height */
  display: flex;
  flex-direction: column;
  background: var(--canvas);
  overflow: hidden;
}

.content-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow: hidden;
  min-height: 0; /* Important for proper flex behavior */
  /* Reserve space for the fixed bottom nav (64px + safe area) */
  padding-bottom: calc(var(--bottom-nav-h, 64px) + env(safe-area-inset-bottom, 0px));
}

.player-container {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex-grow: 0; /* Prevents container from growing */
  flex-shrink: 0; /* Prevents container from shrinking */
}
</style>
